import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { 
  Car, Bike, Truck, CalendarIcon, Clock, 
  ChevronRight, Check, CreditCard, MapPin,
  Users, Fuel, Settings, Shield
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const VEHICLES = [
  // Autos
  {
    id: 'citadine',
    category: 'auto',
    name: 'Citadine',
    description: 'Idéale pour la ville, économique et facile à garer',
    price_day: 35,
    image: 'https://images.unsplash.com/photo-1609521263047-f8f205293f24?w=400',
    specs: { seats: 4, fuel: 'Essence', transmission: 'Manuelle' },
    features: ['Climatisation', 'Bluetooth', 'GPS']
  },
  {
    id: 'berline',
    category: 'auto',
    name: 'Berline Confort',
    description: 'Confort et espace pour vos trajets longue distance',
    price_day: 55,
    image: 'https://images.unsplash.com/photo-1555215695-3004980ad54e?w=400',
    specs: { seats: 5, fuel: 'Diesel', transmission: 'Automatique' },
    features: ['Climatisation', 'Bluetooth', 'GPS', 'Régulateur']
  },
  {
    id: 'suv',
    category: 'auto',
    name: 'SUV Familial',
    description: 'Espace et polyvalence pour toute la famille',
    price_day: 75,
    image: 'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=400',
    specs: { seats: 7, fuel: 'Diesel', transmission: 'Automatique' },
    features: ['Climatisation', 'Bluetooth', 'GPS', 'Caméra recul', 'Toit panoramique']
  },
  // Motos & Scooters
  {
    id: 'scooter-125',
    category: 'moto',
    name: 'Scooter 125cc',
    description: 'Parfait pour circuler en ville rapidement',
    price_day: 25,
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400',
    specs: { seats: 2, fuel: 'Essence', transmission: 'Automatique' },
    features: ['Top case', 'Tablier', 'Antivol']
  },
  {
    id: 'moto-roadster',
    category: 'moto',
    name: 'Moto Roadster',
    description: 'Pour vos balades et trajets quotidiens',
    price_day: 65,
    image: 'https://images.unsplash.com/photo-1558981806-ec527fa84c39?w=400',
    specs: { seats: 2, fuel: 'Essence', transmission: 'Manuelle' },
    features: ['Sacoches', 'GPS', 'Antivol']
  },
  // Utilitaires
  {
    id: 'utilitaire-3m3',
    category: 'utilitaire',
    name: 'Utilitaire 3m³',
    description: 'Compact et maniable, idéal petits déménagements',
    price_day: 45,
    image: 'https://images.unsplash.com/photo-1632323090811-a63c73580ba9?w=400',
    specs: { volume: '3m³', fuel: 'Diesel', transmission: 'Manuelle' },
    features: ['Hayon', 'Sangles', 'Couvertures']
  },
  {
    id: 'utilitaire-6m3',
    category: 'utilitaire',
    name: 'Utilitaire 6m³',
    description: 'Polyvalent pour déménagements moyens',
    price_day: 55,
    image: 'https://images.unsplash.com/photo-1619642751034-765dfdf7c58e?w=400',
    specs: { volume: '6m³', fuel: 'Diesel', transmission: 'Manuelle' },
    features: ['Hayon', 'Sangles', 'Couvertures', 'Diable']
  },
  {
    id: 'utilitaire-12m3',
    category: 'utilitaire',
    name: 'Utilitaire 12m³',
    description: 'Grande capacité pour gros déménagements',
    price_day: 85,
    image: 'https://images.unsplash.com/photo-1586191582066-3b9c22f5d71d?w=400',
    specs: { volume: '12m³', fuel: 'Diesel', transmission: 'Manuelle' },
    features: ['Hayon élévateur', 'Sangles', 'Couvertures', 'Diable', 'Rampe']
  },
  {
    id: 'utilitaire-20m3',
    category: 'utilitaire',
    name: 'Utilitaire 20m³',
    description: 'Capacité maximale pour déménagements complets',
    price_day: 120,
    image: 'https://images.unsplash.com/photo-1601628828688-632f38a5a7d0?w=400',
    specs: { volume: '20m³', fuel: 'Diesel', transmission: 'Manuelle' },
    features: ['Hayon élévateur', 'Sangles', 'Couvertures', 'Diable', 'Rampe', 'GPS']
  },
];

const CATEGORIES = [
  { id: 'all', name: 'Tous', icon: Settings },
  { id: 'auto', name: 'Voitures', icon: Car },
  { id: 'moto', name: 'Motos & Scooters', icon: Bike },
  { id: 'utilitaire', name: 'Utilitaires', icon: Truck },
];

export default function LocationPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('all');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const filteredVehicles = activeCategory === 'all' 
    ? VEHICLES 
    : VEHICLES.filter(v => v.category === activeCategory);

  const calculateDays = () => {
    if (!startDate || !endDate) return 0;
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays || 1;
  };

  const calculateTotal = () => {
    if (!selectedVehicle) return 0;
    const days = calculateDays();
    return selectedVehicle.price_day * days;
  };

  const handleReservation = async () => {
    if (!user) {
      toast.error('Veuillez vous connecter pour réserver');
      navigate('/connexion');
      return;
    }

    if (!selectedVehicle || !startDate || !endDate) {
      toast.error('Veuillez sélectionner un véhicule et des dates');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Create rental booking
      const bookingResponse = await fetch(`${API_URL}/api/rentals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          vehicle_id: selectedVehicle.id,
          vehicle_name: selectedVehicle.name,
          category: selectedVehicle.category,
          start_date: format(startDate, 'yyyy-MM-dd'),
          end_date: format(endDate, 'yyyy-MM-dd'),
          days: calculateDays(),
          price_per_day: selectedVehicle.price_day,
          total_price: calculateTotal()
        })
      });

      if (!bookingResponse.ok) {
        throw new Error('Erreur lors de la réservation');
      }

      const booking = await bookingResponse.json();

      // Create checkout session
      const checkoutResponse = await fetch(`${API_URL}/api/rentals/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          rental_id: booking.id,
          origin_url: window.location.origin
        })
      });

      if (!checkoutResponse.ok) {
        throw new Error('Erreur lors du paiement');
      }

      const checkout = await checkoutResponse.json();
      window.location.href = checkout.url;

    } catch (error) {
      console.error('Reservation error:', error);
      toast.error('Erreur lors de la réservation. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Hero */}
      <section className="bg-gradient-to-b from-zinc-900 to-[#09090B] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-blue-600/20 text-blue-400 px-4 py-2 rounded-full border border-blue-600/30 mb-6">
            <Car size={18} />
            <span className="text-sm font-medium">Location de véhicules</span>
          </div>
          <h1 className="text-5xl font-bold text-white mb-4">
            LOCATION <span className="text-red-500">VÉHICULES</span>
          </h1>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            Voitures, motos, scooters et utilitaires de 3m³ à 20m³. Réservez en ligne et payez en toute sécurité.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="border-b border-zinc-800 sticky top-20 z-30 bg-[#09090B]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 py-4 overflow-x-auto">
            {CATEGORIES.map((cat) => (
              <Button
                key={cat.id}
                variant={activeCategory === cat.id ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveCategory(cat.id)}
                className={activeCategory === cat.id 
                  ? 'bg-red-600 text-white' 
                  : 'text-zinc-400 hover:text-white'
                }
                data-testid={`filter-location-${cat.id}`}
              >
                <cat.icon size={16} className="mr-2" />
                {cat.name}
              </Button>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Vehicles Grid */}
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold text-white mb-6">
              {activeCategory === 'all' ? 'Tous nos véhicules' : CATEGORIES.find(c => c.id === activeCategory)?.name}
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {filteredVehicles.map((vehicle, idx) => (
                <Card 
                  key={vehicle.id}
                  className={`bg-zinc-900/50 border-zinc-800 overflow-hidden cursor-pointer transition-all card-hover ${
                    selectedVehicle?.id === vehicle.id ? 'ring-2 ring-red-500 border-red-500/50' : ''
                  }`}
                  onClick={() => setSelectedVehicle(vehicle)}
                  data-testid={`vehicle-${vehicle.id}`}
                >
                  <div className="relative h-40 overflow-hidden">
                    <img 
                      src={vehicle.image} 
                      alt={vehicle.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 right-2 bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                      {vehicle.price_day}€/jour
                    </div>
                    {selectedVehicle?.id === vehicle.id && (
                      <div className="absolute top-2 left-2 bg-green-500 text-white p-1 rounded-full">
                        <Check size={16} />
                      </div>
                    )}
                  </div>
                  
                  <CardContent className="p-4">
                    <h3 className="text-lg font-bold text-white mb-1">{vehicle.name}</h3>
                    <p className="text-zinc-400 text-sm mb-3">{vehicle.description}</p>
                    
                    <div className="flex flex-wrap gap-2 mb-3">
                      {vehicle.specs.seats && (
                        <span className="text-xs bg-zinc-800 text-zinc-300 px-2 py-1 rounded">
                          <Users size={12} className="inline mr-1" />
                          {vehicle.specs.seats} places
                        </span>
                      )}
                      {vehicle.specs.volume && (
                        <span className="text-xs bg-zinc-800 text-zinc-300 px-2 py-1 rounded">
                          <Truck size={12} className="inline mr-1" />
                          {vehicle.specs.volume}
                        </span>
                      )}
                      <span className="text-xs bg-zinc-800 text-zinc-300 px-2 py-1 rounded">
                        <Fuel size={12} className="inline mr-1" />
                        {vehicle.specs.fuel}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap gap-1">
                      {vehicle.features.slice(0, 3).map((feature, i) => (
                        <span key={i} className="text-xs text-zinc-500">
                          {i > 0 && '•'} {feature}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Reservation Panel */}
          <div className="lg:col-span-1">
            <Card className="bg-zinc-900/50 border-zinc-800 sticky top-40">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <CalendarIcon className="text-red-500" size={24} />
                  Réservation
                </h3>

                {/* Selected Vehicle */}
                {selectedVehicle ? (
                  <div className="bg-zinc-800/50 rounded-lg p-4 mb-6">
                    <p className="text-zinc-400 text-sm">Véhicule sélectionné</p>
                    <p className="text-white font-bold">{selectedVehicle.name}</p>
                    <p className="text-red-500 font-bold">{selectedVehicle.price_day}€/jour</p>
                  </div>
                ) : (
                  <div className="bg-zinc-800/50 rounded-lg p-4 mb-6 text-center">
                    <p className="text-zinc-500">Sélectionnez un véhicule</p>
                  </div>
                )}

                {/* Date Selection */}
                <div className="space-y-4 mb-6">
                  <div>
                    <Label className="text-zinc-400 mb-2 block">Date de début</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left bg-zinc-800 border-zinc-700 text-white">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {startDate ? format(startDate, 'dd MMMM yyyy', { locale: fr }) : 'Sélectionner'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800">
                        <Calendar
                          mode="single"
                          selected={startDate}
                          onSelect={setStartDate}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <Label className="text-zinc-400 mb-2 block">Date de fin</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left bg-zinc-800 border-zinc-700 text-white">
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {endDate ? format(endDate, 'dd MMMM yyyy', { locale: fr }) : 'Sélectionner'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800">
                        <Calendar
                          mode="single"
                          selected={endDate}
                          onSelect={setEndDate}
                          disabled={(date) => date < (startDate || new Date())}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Summary */}
                {selectedVehicle && startDate && endDate && (
                  <div className="bg-zinc-800/50 rounded-lg p-4 mb-6">
                    <div className="flex justify-between text-zinc-400 mb-2">
                      <span>Durée</span>
                      <span>{calculateDays()} jour(s)</span>
                    </div>
                    <div className="flex justify-between text-zinc-400 mb-2">
                      <span>Prix/jour</span>
                      <span>{selectedVehicle.price_day}€</span>
                    </div>
                    <div className="border-t border-zinc-700 pt-2 mt-2">
                      <div className="flex justify-between text-white font-bold text-lg">
                        <span>Total</span>
                        <span className="text-red-500">{calculateTotal()}€</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <Button 
                  className="w-full bg-red-600 hover:bg-red-700 text-white btn-glow"
                  size="lg"
                  onClick={handleReservation}
                  disabled={!selectedVehicle || !startDate || !endDate || loading}
                  data-testid="reserve-vehicle-btn"
                >
                  {loading ? (
                    'Chargement...'
                  ) : (
                    <>
                      <CreditCard size={18} className="mr-2" />
                      Réserver et Payer
                    </>
                  )}
                </Button>

                {!user && (
                  <p className="text-zinc-500 text-xs text-center mt-4">
                    Connexion requise pour réserver
                  </p>
                )}

                {/* Info */}
                <div className="mt-6 pt-6 border-t border-zinc-800 space-y-3">
                  <div className="flex items-center gap-2 text-zinc-400 text-sm">
                    <Shield size={16} className="text-green-500" />
                    <span>Assurance tous risques incluse</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-400 text-sm">
                    <MapPin size={16} className="text-red-500" />
                    <span>Retrait au garage - 142 bd Murat</span>
                  </div>
                  <div className="flex items-center gap-2 text-zinc-400 text-sm">
                    <Clock size={16} className="text-blue-500" />
                    <span>Disponible 7j/7 de 8h à 19h30</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Info Section */}
      <section className="py-16 bg-zinc-900/30 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-12">CONDITIONS DE LOCATION</h2>
          
          <div className="grid md:grid-cols-4 gap-6">
            <Card className="bg-zinc-900/50 border-zinc-800 text-center">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="text-red-500" size={28} />
                </div>
                <h3 className="text-white font-bold mb-2">Âge minimum</h3>
                <p className="text-zinc-400 text-sm">21 ans avec 2 ans de permis minimum</p>
              </CardContent>
            </Card>
            
            <Card className="bg-zinc-900/50 border-zinc-800 text-center">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-blue-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="text-blue-500" size={28} />
                </div>
                <h3 className="text-white font-bold mb-2">Caution</h3>
                <p className="text-zinc-400 text-sm">Par empreinte carte bancaire</p>
              </CardContent>
            </Card>
            
            <Card className="bg-zinc-900/50 border-zinc-800 text-center">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="text-green-500" size={28} />
                </div>
                <h3 className="text-white font-bold mb-2">Assurance</h3>
                <p className="text-zinc-400 text-sm">Tous risques incluse dans le tarif</p>
              </CardContent>
            </Card>
            
            <Card className="bg-zinc-900/50 border-zinc-800 text-center">
              <CardContent className="p-6">
                <div className="w-14 h-14 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Fuel className="text-yellow-500" size={28} />
                </div>
                <h3 className="text-white font-bold mb-2">Carburant</h3>
                <p className="text-zinc-400 text-sm">Véhicule rendu avec le plein</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
